import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Chip,
  Select,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  radioGroup: {
    marginBottom: theme.spacing(2),
  },
  autocomplete: {
    marginTop: theme.spacing(2),
  },
  infoText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

// Clients come from api/Client/activeClients (ClientRecordSummary). clientRecordId is the key every rule inclusion stores and the
// value [Authorization].[File].[ClientId] holds, so it is the number shown (and usable as a Main Auth ClientId trigger value).
export const getClientLabel = client => (client ? `${client.shortName || '(no name)'} (${client.clientRecordId})` : '');

const ClientScopeSelector = ({ applyToAllClients, selectedClients, allClients, onChange, reloPolicyId, onFetchReloPolicies }) => {
  const classes = useStyles();
  const [reloPolicies, setReloPolicies] = useState([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);

  // Alphabetical by name, then by client id for identical names
  const sortedClients = useMemo(
    () => [...allClients].sort((a, b) =>
      (a.shortName || '').localeCompare(b.shortName || '', undefined, { sensitivity: 'base' }) || (a.clientRecordId || 0) - (b.clientRecordId || 0)),
    [allClients],
  );

  const isSameClient = (option, value) => option.clientRecordId === value.clientRecordId;

  useEffect(() => {
    if (!applyToAllClients && selectedClients.length === 1) {
      const clientId = selectedClients[0].clientRecordId;
      setIsLoadingPolicies(true);
      onFetchReloPolicies(clientId).then((result) => {
        setReloPolicies(result.response || []);
        setIsLoadingPolicies(false);
      });
    } else {
      setReloPolicies([]);
    }
  }, [applyToAllClients, selectedClients, onFetchReloPolicies]);

  const handleScopeChange = (event) => {
    const value = event.target.value === 'true';
    onChange({ applyToAllClients: value, selectedClients: [], reloPolicyId: null });
  };

  const handleClientsChange = (event, newValue) => {
    // For specific client mode, only allow single selection
    if (!applyToAllClients) {
      onChange({ applyToAllClients, selectedClients: newValue ? [newValue] : [], reloPolicyId: null });
    } else {
      // For exclusion mode, allow multiple
      onChange({ applyToAllClients, selectedClients: newValue });
    }
  };

  const handleReloPolicyChange = (event) => {
    onChange({ applyToAllClients, selectedClients, reloPolicyId: event.target.value || null });
  };

  const getLabel = () => {
    if (applyToAllClients) {
      return 'Exclude these clients';
    }
    return 'Select client';
  };

  const getHelperText = () => {
    if (applyToAllClients) {
      return 'Rule will apply to all clients except the ones selected below.';
    }
    return 'Rule will ONLY apply to this client (optionally filtered by relo policy).';
  };

  return (
    <Paper className={classes.container}>
      <Typography variant="h6" gutterBottom>
        4. Client Scope
      </Typography>

      <FormControl component="fieldset" className={classes.radioGroup}>
        <FormLabel component="legend">Which clients should this rule apply to?</FormLabel>
        <RadioGroup
          value={applyToAllClients.toString()}
          onChange={handleScopeChange}
        >
          <FormControlLabel
            value="true"
            control={<Radio size="small" />}
            label="All clients"
          />
          <FormControlLabel
            value="false"
            control={<Radio size="small" />}
            label="Specific client only"
          />
        </RadioGroup>
      </FormControl>

      {applyToAllClients ? (
        <Autocomplete
          multiple
          className={classes.autocomplete}
          options={sortedClients}
          getOptionLabel={getClientLabel}
          getOptionSelected={isSameClient}
          value={selectedClients}
          onChange={handleClientsChange}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={getLabel()}
              placeholder="Search clients..."
              size="small"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.clientRecordId}
                label={getClientLabel(option)}
                {...getTagProps({ index })}
                size="small"
              />
            ))
          }
        />
      ) : (
        <Box>
          <Autocomplete
            className={classes.autocomplete}
            options={sortedClients}
            getOptionLabel={getClientLabel}
            getOptionSelected={isSameClient}
            value={selectedClients.length > 0 ? selectedClients[0] : null}
            onChange={handleClientsChange}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={getLabel()}
                placeholder="Search clients..."
                size="small"
              />
            )}
          />

          {selectedClients.length > 0 && (
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 16 }}>
              <InputLabel>Relocation Policy (Optional)</InputLabel>
              <Select
                value={reloPolicyId || ''}
                onChange={handleReloPolicyChange}
                label="Relocation Policy (Optional)"
                disabled={isLoadingPolicies}
              >
                <MenuItem value="">
                  <em>All policies</em>
                </MenuItem>
                {reloPolicies.map((policy) => (
                  <MenuItem key={policy.reloPolicyId} value={policy.reloPolicyId}>
                    {policy.policyName}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" style={{ marginTop: 4, color: 'rgba(0, 0, 0, 0.54)' }}>
                Optionally restrict this rule to a specific relocation policy for this client
              </Typography>
            </FormControl>
          )}
        </Box>
      )}

      <Typography variant="body2" className={classes.infoText}>
        {getHelperText()}
      </Typography>

      {applyToAllClients && selectedClients.length > 0 && (
        <Typography variant="body2" className={classes.infoText}>
          <strong>Excluded: </strong>
          {selectedClients.length} client{selectedClients.length !== 1 ? 's' : ''}
        </Typography>
      )}
    </Paper>
  );
};

ClientScopeSelector.propTypes = {
  applyToAllClients: PropTypes.bool.isRequired,
  selectedClients: PropTypes.arrayOf(
    PropTypes.shape({
      clientRecordId: PropTypes.number,
      clientCode: PropTypes.number,
      shortName: PropTypes.string,
    })
  ).isRequired,
  allClients: PropTypes.arrayOf(
    PropTypes.shape({
      clientRecordId: PropTypes.number,
      clientCode: PropTypes.number,
      shortName: PropTypes.string,
    })
  ),
  onChange: PropTypes.func.isRequired,
  onFetchReloPolicies: PropTypes.func.isRequired,
  reloPolicyId: PropTypes.number,
};

ClientScopeSelector.defaultProps = {
  allClients: [],
  reloPolicyId: null,
};

export default ClientScopeSelector;
